var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a = require('../turtle/tokens'), turtleTokenTypes = _a.turtleTokenTypes, turtleTokenMap = _a.turtleTokenMap;
var sparqlTokenMap = require('../sparql/tokens').sparqlTokenMap;
import { createToken, Lexer } from 'chevrotain';
import memoize from 'memoize-one';
import { getAsTypedTuple } from 'helpers/chevrotain/types';
import isDeepEqual from 'lodash.isequal';
var shaclIriNamespace = 'http://www.w3.org/ns/shacl#';
var xsdIriNamespace = 'http://www.w3.org/2001/XMLSchema#';
// Token categories, useful for making the parser rules simpler.
export var categoryTokenMap = {
    ManyIriTakingPredicate: createToken({
        name: 'ManyIriTakingPredicate',
        pattern: Lexer.NA,
    }),
    SingleIriTakingPredicate: createToken({
        name: 'SingleIriTakingPredicate',
        pattern: Lexer.NA,
    }),
    NodeKindIRI: createToken({
        name: 'NodeKindIRI',
        pattern: Lexer.NA,
    }),
    IntTakingPredicate: createToken({
        name: 'IntTakingPredicate',
        pattern: Lexer.NA,
    }),
    StringLiteralQuoteTakingPredicate: createToken({
        name: 'StringLiteralQuoteTakingPredicate',
        pattern: Lexer.NA,
    }),
    LangStringTakingPredicate: createToken({
        name: 'LangStringTakingPredicate',
        pattern: Lexer.NA,
    }),
    BooleanTakingPredicate: createToken({
        name: 'BooleanTakingPredicate',
        pattern: Lexer.NA,
    }),
    ShapeExpectingPredicate: createToken({
        name: 'ShapeExpectingPredicate',
        pattern: Lexer.NA,
    }),
    AnyLiteralTakingPredicate: createToken({
        name: 'AnyLiteralTakingPredicate',
        pattern: Lexer.NA,
    }),
};
export var categoryTokens = Object.keys(categoryTokenMap).map(function (key) { return categoryTokenMap[key]; });
var localNamesByCategory = {
    ManyIriTakingPredicate: getAsTypedTuple('equals', 'disjoint', 'lessThan', 'lessThanOrEquals', 'targetClass', 'targetSubjectsOf', 'targetObjectsOf'),
    SingleIriTakingPredicate: getAsTypedTuple('class', 'datatype', 'severity'),
    NodeKindIRI: getAsTypedTuple('IRI', 'BlankNode', 'Literal', 'BlankNodeOrIRI', 'BlankNodeOrLiteral', 'IRIOrLiteral'),
    IntTakingPredicate: getAsTypedTuple('minCount', 'maxCount', 'minLength', 'maxLength', 'qualifiedMinCount', 'qualifiedMaxCount'),
    StringLiteralQuoteTakingPredicate: getAsTypedTuple('pattern', 'flags', 'prefix', 'namespace'),
    LangStringTakingPredicate: getAsTypedTuple('message', 'labelTemplate'),
    BooleanTakingPredicate: getAsTypedTuple('uniqueLang', 'qualifiedValueShapesDisjoint', 'closed', 'deactivated', 'optional'),
    ShapeExpectingPredicate: getAsTypedTuple('not', 'node', 'property', 'qualifiedValueShape', 'sparql', 'declare', 'prefixes', 'parameter', 'nodeValidator', 'propertyValidator', 'validator'),
    AnyLiteralTakingPredicate: getAsTypedTuple('minExclusive', 'minInclusive', 'maxExclusive', 'maxInclusive'),
    other: getAsTypedTuple('Shape', 'NodeShape', 'PropertyShape', 'targetNode', 'message', 'path', 'alternativePath', 'inversePath', 'zeroOrMorePath', 'oneOrMorePath', 'zeroOrOnePath', 'nodeKind', 'languageIn', 'and', 'or', 'xone', 'ignoredProperties', 'hasValue', 'in', 'select', 'ask'),
};
var xsdLocalNames = getAsTypedTuple('boolean', 'integer', 'string', 'date', 'dateTime', 'anyURI');
// Map of local names back to their categories, for easier lookup:
var localNameToCategoryMap = Object.keys(localNamesByCategory).reduce(function (nameToCategoryMap, category) {
    var categoryLocalNames = localNamesByCategory[category];
    categoryLocalNames.forEach(function (localName) { return (nameToCategoryMap[localName] = category); });
    return nameToCategoryMap;
}, {});
var localNames = Object.keys(localNameToCategoryMap);
// We can pre-compute all tokens for the `xsd` namespace except for those that
// include prefixes (since we don't know a priori what the prefix will be).
// For each XSD local name, we will create a "category" token that will
// ultimately be used as the single token encompassing either the full
// (un-prefixed) IRI or the prefixed name (i.e., `xsd:string). At this point,
// we create only the category token and the full (un-prefixed) IRI token; the
// prefixed token is created later (via `getShaclTokenTypes`) once we actually
// know what the `xsd` prefix is.
var xsdUnprefixedTokenMap = xsdLocalNames.reduce(function (tokenMap, localName) {
    var _a;
    var tokenName = "SHACL_xsd_" + localName; // category token name
    var iriTokenName = tokenName + "_IRI"; // IRI token name
    // Category token that will ultimately select either an XSD IRI or an XSD PN_LOCAL:
    var iriOrPrefixCategoryToken = createToken({
        name: tokenName,
        pattern: Lexer.NA,
    });
    return __assign({}, tokenMap, (_a = {}, _a[tokenName] = iriOrPrefixCategoryToken, _a[iriTokenName] = createToken({
        name: iriTokenName,
        pattern: "<" + xsdIriNamespace + localName,
        categories: [iriOrPrefixCategoryToken, turtleTokenMap.IRIREF],
    }), _a));
}, {});
// We can also pre-compute all SHACL tokens except for those that include
// prefixes (again, since we don't know a priori what the SHACL prefix will be).
// This helps keep our parser quick. We do it in the same way that we did for
// XSD tokens, above -- we create a "category" token for each SHACL local name
// that will be used to match either the full (un-prefixed) IRI or the prefixed
// name (once we know what the SHACL prefix is).
var shaclUnprefixedTokenMap = localNames.reduce(function (tokenMap, localName) {
    var _a;
    var category = localNameToCategoryMap[localName];
    var categoryToken = categoryTokenMap[category];
    var tokenName = "SHACL_" + localName;
    var iriTokenName = tokenName + "_IRI";
    // Category token that will select either a SHACL IRI or a SHACL PN_LOCAL:
    var iriOrPrefixCategoryToken = createToken({
        name: tokenName,
        pattern: Lexer.NA,
        categories: categoryToken ? [categoryToken] : [],
    });
    return __assign({}, tokenMap, (_a = {}, _a[tokenName] = iriOrPrefixCategoryToken, _a[iriTokenName] = createToken({
        name: iriTokenName,
        pattern: "<" + shaclIriNamespace + localName + ">",
        categories: [iriOrPrefixCategoryToken, turtleTokenMap.IRIREF],
    }), _a));
}, xsdUnprefixedTokenMap);
var makePrefixer = function (prefix) { return function (localName) {
    return prefix + ":" + localName;
}; };
// Retrieves the complete map of all SHACL/XSD tokens, given the SHACL and XSD
// prefixes. The map contains, for every local name, a token matching the full
// IRI, a token matching the prefixed local name, and a "category" token that
// matches both. The category token is useful for simplifying parser rules (not
// having to match every SHACL token as both a full IRI and a prefixed local
// name.)
// This function is called by the SHACL parser. It is memoized because the
// arguments are small and unlikely to change often, and the parser needs to be
// fast, so we should avoid re-computing.
export var getShaclTokenMap = memoize(function (prefixes) {
    var prefixWithShacl = makePrefixer(prefixes.shacl);
    var prefixWithXsd = makePrefixer(prefixes.xsd);
    // Add the prefixed local names to the SHACL token map now that we know the
    // prefixes.
    var shaclTokenMap = localNames.reduce(function (tokenMap, localName) {
        var _a;
        var tokenName = "SHACL_" + localName;
        var prefixedTokenName = tokenName + "_prefixed";
        return __assign({}, tokenMap, (_a = {}, _a[prefixedTokenName] = createToken({
            name: prefixedTokenName,
            pattern: prefixWithShacl(localName),
            categories: [tokenMap[tokenName], turtleTokenMap.PNAME_LN],
        }), _a));
    }, shaclUnprefixedTokenMap);
    // Add the prefixed local names to the XSD token map now that we know the
    // prefixes.
    return xsdLocalNames.reduce(function (tokenMap, localName) {
        var _a;
        var tokenName = "SHACL_xsd_" + localName;
        var prefixedTokenName = tokenName + "_prefixed";
        return __assign({}, tokenMap, (_a = {}, _a[prefixedTokenName] = createToken({
            name: prefixedTokenName,
            pattern: "" + prefixWithXsd(localName),
            categories: [tokenMap[tokenName], turtleTokenMap.PNAME_LN],
        }), _a));
    }, shaclTokenMap);
}, isDeepEqual);
// Get the index of PNAME_NS and IRIREF so that we can re-use existing Turtle
// tokens but ensure that our special SHACL/XSD tokens are inserted at the
// right place (since order of tokens matters for chevrotain).
var pnameIndex = turtleTokenTypes.indexOf(sparqlTokenMap.PNAME_NS);
var iriIndex = turtleTokenTypes.indexOf(turtleTokenMap.IRIREF);
// tokenMap keys will need to be sorted in reverse order so that tokens with
// partial overlap are in the right order in the TokenType array.
var reverseSort = function (a, b) {
    // @ts-ignore: unused variable
    var _a = a.split('_'), aIgnored = _a[0], aName = _a[1], aRemainder = _a.slice(2);
    // @ts-ignore: unused variable
    var _b = b.split('_'), bIgnored = _b[0], bName = _b[1], bRemainder = _b.slice(2);
    // Grab the local name and lowercase it:
    var aSortString = (aName === 'xsd' ? aRemainder[0] : aName).toLowerCase();
    var bSortString = (bName === 'xsd' ? bRemainder[0] : bName).toLowerCase();
    if (aSortString === bSortString) {
        // If local names are identical, prefer the one without a suffix to those with suffixes.
        var aSuffix = aName === 'xsd' ? aRemainder[1] : aRemainder[0];
        var bSuffix = bName === 'xsd' ? bRemainder[1] : bRemainder[0];
        if (aSuffix && bSuffix) {
            return 0; // when both local names have suffixes, treat as lexicographically the same for sorting
        }
        else {
            return aSuffix ? 1 : -1;
        }
    }
    else {
        return aSortString < bSortString ? 1 : bSortString < aSortString ? -1 : 0;
    }
};
// Given SHACL and XSD prefixes, this method returns an array of Turtle +
// SHACL/XSD tokens, including tokens for prefixed local names, with the
// SHACL/XSD tokens inserted at the proper positions so that they are matched
// before the more generic Turtle tokens.
export var getShaclTokenTypes = memoize(function (prefixes) {
    var tokenMap = getShaclTokenMap(prefixes);
    var _a = Object.keys(tokenMap)
        .sort(reverseSort)
        .reduce(function (accumulator, key) {
        if (key.endsWith('_IRI')) {
            if (iriIndex < pnameIndex) {
                accumulator.iriTokens.push(tokenMap[key.slice(0, -4)]);
            }
            accumulator.iriTokens.push(tokenMap[key]);
        }
        else if (key.endsWith('_prefixed')) {
            if (pnameIndex < iriIndex) {
                accumulator.pnameTokens.push(tokenMap[key.slice(0, -9)]);
            }
            accumulator.pnameTokens.push(tokenMap[key]);
        }
        return accumulator;
    }, { pnameTokens: [], iriTokens: [] }), pnameTokens = _a.pnameTokens, iriTokens = _a.iriTokens;
    if (pnameIndex < iriIndex) {
        return turtleTokenTypes.slice(0, pnameIndex).concat(categoryTokens, pnameTokens, turtleTokenTypes.slice(pnameIndex, iriIndex), iriTokens, turtleTokenTypes.slice(iriIndex));
    }
    else {
        return turtleTokenTypes.slice(0, iriIndex).concat(categoryTokens, iriTokens, turtleTokenTypes.slice(iriIndex, pnameIndex), pnameTokens, turtleTokenTypes.slice(pnameIndex));
    }
}, isDeepEqual);
